import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { ApiService } from '../shared/api.service';
import {
    Expense,
    FuelExpense,
    TollExpense,
    AccommodationExpense,
    TrafficFineExpense,
    FoodExpense,
    OtherExpense,
    PaymentMethod,
    TollBooth,
    Route,
    Segment,
    FineType,
    ExpenseReport,
    ExpenseSummary,
    ExpenseValidation,
    Deposit,
    DepositListItem,
    SharedExpenseGroup,
    EligibleService,
    SharedHistoryDay,
    SharedDepositGroup,
    UnifiedExpenseItem
} from '../interfaces/expense.interface';

@Injectable({
    providedIn: 'root'
})
export class ExpenseService {
    private endpoint = 'expenses';

    // Catálogos con shareReplay(1): se piden una sola vez por sesión
    private paymentMethods$: Observable<PaymentMethod[]> | null = null;
    private tollBooths$: Observable<TollBooth[]> | null = null;
    private fineTypes$: Observable<FineType[]> | null = null;
    private routes$: Observable<Route[]> | null = null;

    constructor(private apiService: ApiService) { }

    // Expense Report operations
    createExpenseReport(expenseReport: ExpenseReport): Observable<any> {
        return this.apiService.post(this.endpoint, expenseReport);
    }

    getExpenseReport(id: number): Observable<ExpenseReport> {
        return this.apiService.get(`${this.endpoint}/${id}`);
    }

    updateExpenseReport(id: number, expenseReport: ExpenseReport): Observable<any> {
        return this.apiService.put(`${this.endpoint}/${id}`, expenseReport);
    }

    // Get expense by service ID
    getExpenseByServiceId(serviceId: number): Observable<any> {
        return this.apiService.get(`${this.endpoint}/by-service/${serviceId}`);
    }

    // Master data operations — cacheados en memoria para no repetir la petición
    getPaymentMethods(): Observable<PaymentMethod[]> {
        if (!this.paymentMethods$) {
            this.paymentMethods$ = this.apiService.get('payment-methods').pipe(shareReplay(1));
        }
        return this.paymentMethods$;
    }

    getTollBooths(): Observable<TollBooth[]> {
        if (!this.tollBooths$) {
            this.tollBooths$ = this.apiService.get('toll-booths').pipe(shareReplay(1));
        }
        return this.tollBooths$;
    }

    getSegmentsByTollBooth(tollBoothId: number): Observable<Segment[]> {
        return this.apiService.get(`segments/by-toll-booth/${tollBoothId}`);
    }

    getRoutes(): Observable<Route[]> {
        if (!this.routes$) {
            this.routes$ = this.apiService.get('routes').pipe(shareReplay(1));
        }
        return this.routes$;
    }

    getFineTypes(): Observable<FineType[]> {
        if (!this.fineTypes$) {
            this.fineTypes$ = this.apiService.get('fine-types').pipe(shareReplay(1));
        }
        return this.fineTypes$;
    }

    // File upload
    uploadFile(file: File, type: string, index: number): Observable<any> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', type);
        formData.append('index', index.toString());

        return this.apiService.post(`${this.endpoint}/upload`, formData);
    }

    deleteFile(path: string): Observable<any> {
        return this.apiService.delete(`${this.endpoint}/upload`, { path });
    }

    readImage(file: File) {
        const formData = new FormData();

        formData.append("image",file);

        return this.apiService.post(
            "scan-expenses",
            formData
        );
    }

    // Expense Summary
    getExpenseSummary(serviceId: number): Observable<ExpenseSummary> {
        return this.apiService.get(`${this.endpoint}/${serviceId}/summary`);
    }

    downloadSummaryPdf(serviceId: number): Observable<Blob> {
        return this.apiService.getBlob(`${this.endpoint}/${serviceId}/pdf`);
    }

    downloadXls(serviceId: number): Observable<Blob> {
        return this.apiService.getBlob(`${this.endpoint}/${serviceId}/validation/xls`);
    }

    downloadPdf(serviceId: number): Observable<Blob> {
        return this.apiService.getBlob(`${this.endpoint}/${serviceId}/validation/pdf`);
    }

    // Validation
    getExpenseValidation(serviceId: number): Observable<ExpenseValidation> {
        return this.apiService.get(`${this.endpoint}/${serviceId}/validation`);
    }

    approveExpense(expenseItemId: number, comments?: string): Observable<any> {
        return this.apiService.post(`${this.endpoint}/items/${expenseItemId}/approve`, { comments });
    }

    saveEstado(expenseId: number, estado: string | null): Observable<any> {
        return this.apiService.patch(`${this.endpoint}/${expenseId}/estado`, { estado });
    }

    saveKmComparison(expenseId: number, payload: { km_gps: number | null; km_google_maps: number | null }): Observable<any> {
        return this.apiService.patch(`${this.endpoint}/${expenseId}/km-comparison`, payload);
    }

    saveTripDates(expenseId: number, payload: {
        tag_booth_entry_date: string | null; tag_booth_entry_time: string | null;
        tag_booth_exit_date: string | null; tag_booth_exit_time: string | null;
        patio_entry_date: string | null; patio_entry_time: string | null;
        patio_exit_date: string | null; patio_exit_time: string | null;
    }): Observable<any> {
        return this.apiService.patch(`${this.endpoint}/${expenseId}/trip-dates`, payload);
    }

    saveCategoriasIncluidas(expenseId: number, categoriasIncluidas: Record<string, boolean>): Observable<any> {
        return this.apiService.patch(`${this.endpoint}/${expenseId}/categorias-incluidas`, { categorias_incluidas: categoriasIncluidas });
    }

    rejectExpense(expenseItemId: number, reason: string): Observable<any> {
        return this.apiService.post(`${this.endpoint}/items/${expenseItemId}/reject`, { reason });
    }

    requestExpenseInfo(expenseItemId: number, message: string): Observable<any> {
        return this.apiService.post(`${this.endpoint}/items/${expenseItemId}/request-info`, { message });
    }

    // Deposits
    getDepositsList(filters?: any): Observable<DepositListItem[]> {
        return this.apiService.post(`${this.endpoint}/deposits/list`, filters || {});
    }

    getDeposits(serviceId: number): Observable<Deposit[]> {
        return this.apiService.get(`${this.endpoint}/${serviceId}/deposits`);
    }

    createDeposit(deposit: Deposit): Observable<any> {
        return this.apiService.post(`${this.endpoint}/deposits`, deposit);
    }

    updateDeposit(id: number, deposit: Partial<Deposit>): Observable<any> {
        return this.apiService.put(`${this.endpoint}/deposits/${id}`, deposit);
    }

    uploadItemEvidence(type: 'fuel' | 'food' | 'other' | 'accommodation' | 'fine', id: number, file: File): Observable<any> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', type);
        formData.append('id', id.toString());
        return this.apiService.post(`${this.endpoint}/items/evidence`, formData);
    }

    uploadTagTolls(serviceId: number, file: File, trip: 'partida' | 'retorno'): Observable<any> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('trip', trip);
        return this.apiService.post(`${this.endpoint}/toll-tags/${serviceId}`, formData);
    }

    validateItems(expenseId: number, body: {
        food_expenses: { id: number; is_validated: boolean }[];
        other_expenses: { id: number; is_validated: boolean }[];
        accommodation_expenses: { id: number; is_validated: boolean }[];
    }): Observable<any> {
        return this.apiService.patch(`${this.endpoint}/${expenseId}/validate-items`, body);
    }

    saveDevolucion(expenseId: number, amount: number): Observable<any> {
        return this.apiService.patch(`${this.endpoint}/${expenseId}/devolucion`, { amount });
    }

    validateExpense(expenseId: number, payload: { is_valid: boolean; validator_name: string; inconsistency?: string | null }): Observable<any> {
        return this.apiService.patch(`${this.endpoint}/${expenseId}/validate-expense`, payload);
    }

    // Shared expense groups
    getSharedGroupByService(serviceId: number): Observable<SharedExpenseGroup | { group_id: null }> {
        return this.apiService.get(`${this.endpoint}/shared-groups/by-service/${serviceId}`);
    }

    getEligibleServicesForUnification(serviceId: number): Observable<{ data: EligibleService[] }> {
        return this.apiService.get(`${this.endpoint}/shared-groups/eligible-services/${serviceId}`);
    }

    getEligibleServicesByUnitDriver(unitId: number, driverId: number): Observable<{ data: any[] }> {
        return this.apiService.get(`${this.endpoint}/shared-groups/eligible-by-unit-driver?unit_id=${unitId}&driver_id=${driverId}`);
    }

    createSharedGroup(sourceServiceId: number, targetServiceIds: number[]): Observable<any> {
        return this.apiService.post(`${this.endpoint}/shared-groups`, {
            source_service_id: sourceServiceId,
            target_service_ids: targetServiceIds
        });
    }

    dissolveSharedGroup(groupId: number): Observable<any> {
        return this.apiService.delete(`${this.endpoint}/shared-groups/${groupId}`, {});
    }

    leaveSharedGroup(groupId: number, serviceId: number): Observable<any> {
        return this.apiService.patch(`${this.endpoint}/shared-groups/${groupId}/service/${serviceId}/leave`, {});
    }

    getSharedExpenseHistory(serviceId: number): Observable<{ data: SharedHistoryDay[] }> {
        return this.apiService.get(`${this.endpoint}/shared-groups/history/${serviceId}`);
    }

    getGroupUnifiedExpenses(groupId: number): Observable<any> {
        return this.apiService.get(`${this.endpoint}/shared-groups/${groupId}/unified-expenses`);
    }

    getSharedDepositsList(filters?: any): Observable<{ data: SharedDepositGroup[] }> {
        return this.apiService.post(`${this.endpoint}/deposits/shared-list`, filters || {});
    }

    createUnifiedExpense(groupId: number, data: {
        expense_type: string;
        amount: number;
        payment_method_id: number;
        description?: string;
    }): Observable<any> {
        return this.apiService.post(`${this.endpoint}/shared-groups/${groupId}/expense`, data);
    }

    bulkUpdateSharedItems(items: Array<{ type: string; id: number; [key: string]: any }>): Observable<any> {
        return this.apiService.patch(`${this.endpoint}/items/update-shared`, { items });
    }

    deleteSharedItem(type: string, id: number): Observable<any> {
        return this.apiService.delete(`${this.endpoint}/items/shared/${type}/${id}`, {});
    }
}
